import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppCard from '@/components/appcard/AppCard.tsx';
import Breadcrumb from './Breadcrumb.tsx';
import { MenuData } from '@core/data/menuData.ts';
import { useSettingsStore } from '@core/store';

export interface MenuItem {
    title?: string;
    description?: string;
    path: string;
    items?: MenuItem[];
}

export interface RouteItem {
    path: string;
    meta?: {
        breadcrumb?: {
            breadcrumbActive?: string;
            breadcrumbInactive?: string;
        };
    };
}

export interface PageTitleBarProps {
    // Navigation props
    menus?: MenuData;
    currentRoute?: RouteItem;

    // Translation function
    t: (key: string) => string;

    // Content slots
    children?: React.ReactNode;
    titleSlot?: React.ReactNode;
}

const PageTitleBar: React.FC<PageTitleBarProps> = ({ menus, currentRoute, t, children, titleSlot }) => {
    const { darkMode } = useSettingsStore();
    const location = useLocation();

    const baseLocaleCompare = (str1: string, str2: string): boolean => {
        return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
    };

    const findMenuItem = (menuItems: any[], resolvedPath: string): any => {
        if (!menuItems) return null;

        for (let i = 0; i < menuItems.length; i += 1) {
            const menu = menuItems[i];
            if (baseLocaleCompare(resolvedPath, menu?.path)) {
                return menu;
            }
            const menuItem = findMenuItem(menu.items, resolvedPath);
            if (menuItem) {
                return menuItem;
            }
        }
        return null;
    };

    const menuItem = useMemo(() => {
        if (!currentRoute || !menus) return {};

        const { path } = currentRoute;
        const resolvedPath = path;

        const menuValues = Object.values(menus);
        const foundMenuItem = findMenuItem(menuValues, resolvedPath);
        return foundMenuItem || {};
    }, [currentRoute, menus]);

    const activeBreadcrumb = currentRoute?.meta?.breadcrumb?.breadcrumbActive;
    const inactiveBreadcrumb = currentRoute?.meta?.breadcrumb?.breadcrumbInactive;

    const activeBreadcrumbDefault = (location.state as any)?.breadcrumbActive;
    const inactiveBreadcrumbDefault = (location.state as any)?.breadcrumbInactive;

    return (
        <Box className="v-container v-container--fluid">
            <AppCard fullBlock={true} customClasses="title-bar">
                <Breadcrumb
                    menuItem={menuItem}
                    activeBreadcrumb={activeBreadcrumb ? activeBreadcrumb : activeBreadcrumbDefault}
                    inactiveBreadcrumb={inactiveBreadcrumb ? inactiveBreadcrumb : inactiveBreadcrumbDefault}
                    titleSlot={titleSlot}
                    t={t}
                />
                <Box className="title-bar-actions">{children}</Box>
            </AppCard>
        </Box>
    );
};

export default PageTitleBar;
